import { organization } from "./data/organization";
import { workshopArticles } from "./data/workshopArticles";

export const siteOrigin = "https://www.promovareasanatatii.ro";
const brand = "AUPPS Personocrația";
const defaultImage = "/assets/acasa/home-hero-wellbeing.png";

export type SeoPage = {
  path: string;
  title: string;
  description: string;
  heading: string;
  image?: string;
  kind?: "article";
  contentPath?: string;
  noindex?: boolean;
};

const mainPages: SeoPage[] = [
  { path: "/", title: `${brand} | Asociația pentru promovarea sănătății`, heading: "Cunoașterea ne eliberează și ne vindecă", description: "Asociația Umanitară pentru Promovarea Sănătății Personocrația organizează ateliere, tabere și întâlniri pentru adulți, copii și familii.", image: defaultImage },
  { path: "/despre-noi/", title: `Despre noi | ${brand}`, heading: "Despre Asociația Personocrația", description: "Află cine suntem, cum a început asociația în 2011 și ce obiective avem pentru comunitate.", image: defaultImage },
  { path: "/stiinta-vindecarii-germanice/", title: `Știința Vindecării Germanice | ${brand}`, heading: "Știința Vindecării Germanice", description: "Citește prezentarea asociației despre Știința Vindecării Germanice și perspectiva lui Ryke Geerd Hamer.", image: "/articles/pagini/stiinta-vindecarii-germanice/germanic-banner.png", contentPath: "/articles/pagini/stiinta-vindecarii-germanice/stiinta-vindecarii-germanice.md" },
  { path: "/personocratia/", title: `Personocrația | ${brand}`, heading: "Personocrația", description: "Descoperă textul asociației despre Personocrație, Ghislaine Lanctôt și atelierul dedicat cunoașterii de sine.", image: "/articles/pagini/personocratia/ghislaine-lanctot.jpg", contentPath: "/articles/pagini/personocratia/personocratia.md" },
  { path: "/ateliere/", title: `Ateliere și tabere | ${brand}`, heading: "Ateliere și tabere", description: "Vezi programele, atelierele și taberele organizate de Asociația Personocrația pentru adulți, copii și familii." },
  { path: "/articole/", title: `Articole și noutăți | ${brand}`, heading: "Articole și noutăți", description: "Citește articolele și anunțurile despre ateliere, tabere și activitățile comunității Personocrația." },
  { path: "/testimoniale/", title: `Testimoniale | ${brand}`, heading: "Testimoniale din comunitate", description: "Citește testimonialele participanților la atelierele și taberele Asociației Personocrația, în propriile lor cuvinte." },
  { path: "/contact/", title: `Contact și datele asociației | ${brand}`, heading: "Contactează Asociația Personocrația", description: `Scrie-ne despre ateliere, voluntariat sau parteneriate. Găsește datele oficiale, telefonul, adresa și contactele locale ale ${organization.name}.` },
  { path: "/politica-de-confidentialitate/", title: `Politica de confidențialitate | ${brand}`, heading: "Politica de confidențialitate", description: "Află cum Asociația Personocrația folosește datele trimise prin formulare și cum funcționează Google Analytics pe acest site." },
];

function excerpt(text: string, limit = 155): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= limit) return clean;
  return `${clean.slice(0, limit - 1).replace(/\s+\S*$/, "").trimEnd()}…`;
}

export const seoPages: SeoPage[] = [
  ...mainPages.map(page => ({ ...page, image: page.image || defaultImage })),
  ...workshopArticles.map(article => ({
    path: `/articole/${article.slug}/`,
    title: `${article.title} | ${brand}`,
    heading: article.title,
    description: excerpt(article.description),
    image: article.image,
    kind: "article" as const,
    contentPath: article.contentPath,
  })),
];

export function normalizePath(pathname: string): string {
  if (pathname === "/") return "/";
  return `${pathname.replace(/\/+$/, "")}/`;
}

export function getSeoPage(pathname: string): SeoPage | undefined {
  return seoPages.find(page => page.path === normalizePath(pathname));
}

export function absoluteUrl(path: string): string {
  return `${siteOrigin}${path}`;
}

export function structuredData(page: SeoPage): Record<string, unknown>[] {
  const url = absoluteUrl(page.path);
  const organizationId = `${siteOrigin}/#organization`;
  const objects: Record<string, unknown>[] = [];
  if (page.path === "/" || page.path === "/despre-noi/") {
    objects.push({
      "@context": "https://schema.org",
      "@type": "NGO",
      "@id": organizationId,
      name: organization.name,
      alternateName: "AUPPS Personocrația",
      url: `${siteOrigin}/`,
      logo: absoluteUrl("/assets/branding/logo_aupps_clean_high_res.png"),
      email: organization.email,
      telephone: "+40747179922",
      taxID: organization.cif,
      address: { "@type": "PostalAddress", streetAddress: "Sat Baba nr. 68", addressLocality: "Horea", addressRegion: "Alba", addressCountry: "RO" },
    });
  }
  if (page.kind === "article") {
    objects.push({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: page.heading,
      description: page.description,
      mainEntityOfPage: url,
      image: page.image ? absoluteUrl(page.image) : undefined,
      author: { "@type": "Organization", "@id": organizationId, name: organization.name, url: `${siteOrigin}/` },
      publisher: { "@type": "Organization", "@id": organizationId, name: organization.name, url: `${siteOrigin}/` },
    });
  }
  if (page.kind === "article") {
    objects.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Acasă", item: `${siteOrigin}/` },
        { "@type": "ListItem", position: 2, name: "Articole", item: absoluteUrl("/articole/") },
        { "@type": "ListItem", position: 3, name: page.heading, item: url },
      ],
    });
  }
  return objects;
}
